<template>
  <div class="w-full max-w-4xl mx-auto bg-white rounded-2xl">
    <div v-if="!testInfo" class="py-10 flex items-center justify-center text-gray-500">
      Загрузка...
    </div>
    <template v-else>
    <!-- Заголовок -->
    <div class="py-6 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <div class="text-3xl font-bold text-gray-900">{{ testInfo.title }}</div>
        <button
          @click="$emit('close')"
          class="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
        >
          <svg class="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Основная информация -->
    <div class="py-6">
      <!-- Статистика теста -->
      <div class="flex items-center gap-4 mb-4">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
            <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <div class="text-sm text-gray-500">Вопросов</div>
            <div class="text-xl font-semibold text-gray-900">{{ (testInfo.numberOfQuestionsToDisplay ?? testInfo.questions.length) || 0 }}</div>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-yellow-100 rounded-2xl flex items-center justify-center">
            <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <div class="text-sm text-gray-500">Ограничение</div>
            <div class="text-xl font-semibold text-gray-900">{{ testInfo.timeLimit }} мин</div>
          </div>
        </div>
      </div>

      <!-- Связанные уроки -->
      <div v-if="relatedLessons.length > 0 && false" class="mb-4">
        <div class="text-lg font-semibold text-gray-900 mb-4">Тест включает вопросы по следующим урокам:</div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="lesson in relatedLessons"
            :key="lesson.id"
            class="bg-gray-50 rounded-2xl p-3 hover:bg-gray-100 transition-colors cursor-pointer"
            @click="openLesson(lesson)"
          >
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-white text-gray-900 rounded-lg flex items-center justify-center text-sm font-medium">
                {{ lesson.index }}
              </div>
              <div class="flex-1">
                <div class="font-medium text-gray-900 text-sm">{{ lesson.title }}</div>
                <div class="text-xs text-gray-600 flex items-center gap-1 mt-1">
                  <span>Открыть урок</span>
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Правила прохождения -->
      <div class="bg-gray-50 rounded-2xl p-4 mb-10">
        <div class="text-lg font-semibold text-gray-900 mb-6">Тип прохождения теста</div>
        
        <div class="space-y-5">
          <!-- Контроль неправильных ответов -->
          <div class="flex items-start gap-3">
            <div class="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mt-1">
              <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
            </div>
            <div>
              <div class="font-medium text-red-800">В тесте включен контроль неправильных ответов</div>
              <div class="text-sm text-red-700">Если ошибок уже слишком много и на проходной балл не хватит - тест завершится досрочно.</div>
            </div>
          </div>

          <!-- Запрет на переход -->
          <div class="flex items-start gap-3">
            <div class="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mt-1">
              <svg class="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
            </div>
            <div>
              <div class="font-medium text-yellow-800">Запрещен переход к следующему вопросу без ответа на текущий вопрос</div>
              <div class="text-sm text-yellow-700">Нельзя изменить свои ответы.</div>
            </div>
          </div>

          <!-- Информация о вопросах -->
          <div class="flex items-start gap-3">
            <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mt-1">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div>
              <div class="font-medium text-blue-800">Вопросов в тесте {{ (testInfo.numberOfQuestionsToDisplay ?? testInfo.questions.length) || 0 }}</div>
              <div class="text-sm text-blue-700">На выполнение теста выделяется {{ testInfo.timeLimit }} мин</div>
            </div>
          </div>

          <!-- Проходной балл -->
          <div class="flex items-start gap-3">
            <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mt-1">
              <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <div>
              <div class="font-medium text-green-800">Для прохождения теста необходимо ответить верно на {{ Math.round((testInfo.requiredCorrectAnswers / ((testInfo.numberOfQuestionsToDisplay ?? testInfo.questions.length) || 1)) * 100) }}% вопросов</div>
              <div class="text-sm text-green-700">(набрать {{ testInfo.requiredCorrectAnswers }} баллов из {{ (testInfo.numberOfQuestionsToDisplay ?? testInfo.questions.length) || 0 }})</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Кнопки действий -->
      <div class="flex gap-4 justify-end">
        <button
          @click="$emit('close')"
          class="px-8 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
        >
          Вернуться к списку тестов
        </button>
        <button
          @click="handleStartClick"
          class="px-8 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors flex items-center justify-center !text-center"
          :disabled="isStarting"
        >
          <svg v-if="isStarting" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ isStarting ? 'Подготовка...' : 'Приступить к тесту' }}
        </button>
      </div>
    </div>
    </template>
  </div>

  <!-- Модальное окно для урока -->
  <transition name="modal-fade">
    <div v-if="selectedLesson" class="fixed inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <transition name="modal-scale">
        <div class="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-xl border border-gray-200">
          <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div class="text-lg font-semibold text-gray-900">{{ selectedLesson.title }}</div>
            <button @click="selectedLesson = null" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          
          <div class="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <div v-html="normalizedLessonContent" class="prose max-w-none"></div>
          </div>
        </div>
      </transition>
    </div>
  </transition>

  <!-- Предупреждение о наличии уроков перед тестом -->
  <transition name="modal-fade">
    <div v-if="showLessonsWarning" class="fixed inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <transition name="modal-scale">
        <div class="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-xl border border-gray-200">
          <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div class="text-lg font-semibold text-gray-900">Обучающие материалы</div>
            <button @click="showLessonsWarning = false" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="p-6">
            <p class="text-gray-700 mb-6">В данном курсе есть обучающий материал. Рекомендуем сначала изучить уроки перед прохождением теста.</p>
            <div class="flex gap-3 justify-end">
              <button @click="goToLessons" class="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Начать изучение</button>
              <button @click="proceedToTestFromWarning" class="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Перейти к тестированию</button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue'
import apiService from '@/services/api'
import { useToast } from "vue-toastification";

const props = defineProps({
  courseId: {
    type: String,
    required: true
  },
  testId: {
    type: String,
    required: false
  }
})

const emit = defineEmits(['close', 'startTest', 'openLessons'])

const toast = useToast();

// Функция нормализации видео - заменяет iframe на video теги
const normalizeVideoEmbeds = (html) => {
  if (!html) return ''
  if (typeof window === 'undefined' || typeof document === 'undefined') return html
  
  const container = document.createElement('div')
  container.innerHTML = html
  
  const iframes = container.querySelectorAll('iframe.ql-video')
  if (!iframes.length) return html
  
  const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.m4v']
  
  iframes.forEach((iframe) => {
    const src = iframe.getAttribute('src') || ''
    const lowerSrc = src.toLowerCase()
    const hasVideoExtension = VIDEO_EXTENSIONS.some((ext) => lowerSrc.includes(ext))
    
    if (!src || !hasVideoExtension) return
    
    const video = document.createElement('video')
    video.className = iframe.className || 'ql-video'
    video.setAttribute('controls', '')
    video.setAttribute('playsinline', '')
    video.setAttribute('preload', 'metadata')
    video.style.width = '100%'
    video.style.display = 'block'
    video.src = src
    
    if (iframe.parentNode) {
      iframe.parentNode.replaceChild(video, iframe)
    }
  })
  
  return container.innerHTML
}

// Нормализованный контент выбранного урока
const normalizedLessonContent = computed(() => {
  return normalizeVideoEmbeds(selectedLesson.value?.content || '')
})

const testInfo = ref(null)
const relatedLessons = ref([])
const isStarting = ref(false)
const selectedLesson = ref(null)
const showLessonsWarning = ref(false)

const loadTestInfo = async () => {
  try {
    if (props.testId) {
      // Загружаем конкретный тест
      testInfo.value = await apiService.getTest(props.courseId, props.testId)
    } else {
      // Загружаем информацию о курсе с тестами
      const courseData = await apiService.getCourse(props.courseId)
      
      if (courseData.tests && courseData.tests.length > 0) {
        // Берем первый тест (можно доработать для выбора конкретного теста)
        testInfo.value = courseData.tests[0]
      }
    }
    
    // Загружаем информацию о курсе для получения связанных уроков
    const courseData = await apiService.getCourse(props.courseId)
    if (courseData.lessons) {
      relatedLessons.value = courseData.lessons.map((lesson, index) => ({
        ...lesson,
        index: index + 1
      }))
    }
    
  } catch (error) {
    console.error('Ошибка при загрузке информации о тесте:', error)
  }
}

const startTest = async () => {
  if (isStarting.value || !testInfo.value) return
  
  try {
    isStarting.value = true
    
    // Вызываем startTest API для получения данных о тесте и попытке
    const response = await apiService.startTest(props.courseId, testInfo.value.id)
    
    // Передаем данные о тесте родительскому компоненту
    emit('startTest', response)
    
  } catch (error) {
    console.error('Ошибка при запуске теста:', error)
    
    // Показываем более детальную ошибку пользователю
    let errorMessage = 'Ошибка при запуске теста. Попробуйте позже.'
    
    if (error.response?.status === 403) {
      errorMessage = 'У вас не осталось попыток для прохождения этого теста.'
    } else if (error.response?.status === 409) {
      errorMessage = 'У вас уже есть активная попытка прохождения теста.'
    }
    
    toast.error(errorMessage)
  } finally {
    isStarting.value = false
  }
}

const handleStartClick = () => {
  if ((relatedLessons.value?.length || 0) > 0) {
    showLessonsWarning.value = true
    return
  }
  startTest()
}

const goToLessons = () => {
  showLessonsWarning.value = false
  emit('openLessons')
}

const proceedToTestFromWarning = async () => {
  showLessonsWarning.value = false
  await startTest()
}

const openLesson = (lesson) => {
  selectedLesson.value = lesson
}

onMounted(() => {
  loadTestInfo()
})
</script>

<style scoped>
/* Дополнительные стили при необходимости */
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;
}

.modal-scale-enter-active, .modal-scale-leave-active {
  transition: all 0.3s ease;
}

.modal-scale-enter-from, .modal-scale-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

/* Стили для контента урока */
:deep(.prose) {
  line-height: 1.7;
  color: #374151;
}

:deep(.prose h1) {
  font-size: 2rem;
  font-weight: 700;
  margin: 2rem 0 1rem 0;
  color: #111827;
}

:deep(.prose h2) {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.5rem 0 1rem 0;
  color: #111827;
}

:deep(.prose h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem 0;
  color: #111827;
}

:deep(.prose p) {
  margin-bottom: 1rem;
}

:deep(.prose ul) {
  margin: 1rem 0;
  padding-left: 2rem;
}

:deep(.prose ol) {
  margin: 1rem 0;
  padding-left: 2rem;
}

:deep(.prose li) {
  margin-bottom: 0.5rem;
}

:deep(.prose a) {
  color: #574AEA;
  text-decoration: underline;
}

:deep(.prose a:hover) {
  color: #473add;
}

/* Мобильная адаптивность */
@media (max-width: 768px) {
  .w-full.max-w-4xl {
    max-width: 100%;
    width: 100%;
    margin: 0;
    height: 100vh;
    border-radius: 0;
  }
  
  /* Header */
  .py-6 {
    padding: 16px;
  }
  
  .text-3xl {
    font-size: 1.5rem;
    line-height: 1.3;
  }
  
  .w-10.h-10 {
    width: 36px;
    height: 36px;
  }
  
  /* Основное содержимое */
  .py-6:last-child {
    padding: 16px;
    overflow-y: auto;
    max-height: calc(100vh - 100px);
  }
  
  /* Статистика теста */
  .flex.items-center.gap-4 {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .flex.items-center.gap-3 {
    gap: 12px;
    padding: 12px;
    background: #f8fafc;
    border-radius: 12px;
  }
  
  .w-12.h-12 {
    width: 40px;
    height: 40px;
  }
  
  .text-xl {
    font-size: 1.125rem;
  }
  
  /* Связанные уроки */
  .text-lg {
    font-size: 1rem;
    margin-bottom: 12px;
  }
  
  .grid.grid-cols-1.md\:grid-cols-2.lg\:grid-cols-3 {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .rounded-2xl.p-3 {
    padding: 12px;
    border-radius: 12px;
  }
  
  .w-8.h-8 {
    width: 32px;
    height: 32px;
  }
  
  .text-sm {
    font-size: 0.875rem;
  }
  
  .text-xs {
    font-size: 0.75rem;
  }
  
  /* Правила прохождения */
  .rounded-2xl.p-4 {
    padding: 16px;
    border-radius: 16px;
  }
  
  .space-y-5 {
    gap: 16px;
  }
  
  .space-y-5 > * + * {
    margin-top: 16px;
  }
  
  /* Кнопки действий */
  .flex.gap-4 {
    flex-direction: column;
    gap: 12px;
    padding-top: 16px;
  }
  
  .px-8.py-3 {
    padding: 14px 16px;
    font-size: 0.875rem;
    min-height: 48px;
    border-radius: 12px;
  }
  
  /* Модальное окно урока */
  .max-w-4xl.w-full {
    max-width: 100%;
    width: 100%;
    height: 100vh;
    border-radius: 0;
    margin: 0;
  }
  
  .max-h-\[90vh\] {
    max-height: 100vh;
  }
  
  .p-6 {
    padding: 16px;
  }
  
  .px-6.py-4 {
    padding: 12px 16px;
  }
  
  .max-h-\[calc\(90vh-80px\)\] {
    max-height: calc(100vh - 80px);
  }
}

@media (max-width: 480px) {
  .py-6 {
    padding: 12px;
  }
  
  .text-3xl {
    font-size: 1.25rem;
  }
  
  .py-6:last-child {
    padding: 12px;
  }
  
  .flex.items-center.gap-3 {
    gap: 8px;
    padding: 8px;
  }
  
  .w-12.h-12 {
    width: 32px;
    height: 32px;
  }
  
  .text-xl {
    font-size: 1rem;
  }
  
  .grid.grid-cols-1.md\:grid-cols-2.lg\:grid-cols-3 {
    gap: 8px;
  }
  
  .rounded-2xl.p-3 {
    padding: 8px;
  }
  
  .rounded-2xl.p-4 {
    padding: 12px;
  }
  
  .px-8.py-3 {
    padding: 12px 16px;
    font-size: 0.8rem;
    min-height: 44px;
  }
  
  .p-6 {
    padding: 12px;
  }
  
  .px-6.py-4 {
    padding: 8px 12px;
  }
}
</style> 
